import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { FaBriefcase, FaCalendarAlt, FaMapMarkerAlt, FaSearch } from "react-icons/fa";
import PublicHeader from "../../components/PublicHeader";
import PublicFooter from "../../components/PublicFooter";
import { supabase } from "../../supabaseClient";

function formatDate(value) {
  if (!value) return "No closing date";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No closing date";
  return date.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
}

export default function PublicVacancies({ session, profile }) {
  const [vacancies, setVacancies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let active = true;
    supabase
      .from("public_vacancies")
      .select("id, title, description, department, location, closing_date, created_at")
      .order("created_at", { ascending: false })
      .then(({ data, error: fetchError }) => {
        if (!active) return;
        if (fetchError) {
          console.error(fetchError);
          setError("Vacancies could not be loaded right now.");
        } else {
          setVacancies(data || []);
        }
        setLoading(false);
      });
    return () => { active = false; };
  }, []);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return vacancies;
    return vacancies.filter((vacancy) =>
      [vacancy.title, vacancy.description, vacancy.department, vacancy.location]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  }, [search, vacancies]);

  return (
    <div className="pguh-public-page-shell">
      <PublicHeader session={session} profile={profile} />
      <main id="main-content" tabIndex="-1" className="pguh-public-standard-page">
        <div className="nhsuk-width-container">
          <header className="pguh-public-page-heading">
            <span className="pguh-public-kicker">Careers at RUHT</span>
            <h1>Vacancies</h1>
            <p>Explore current opportunities and submit your application online.</p>
          </header>

          <div className="pguh-public-searchbar">
            <FaSearch aria-hidden="true" />
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search by role, department or location"
              aria-label="Search vacancies"
            />
          </div>

          {error && <div className="pguh-public-error" role="alert">{error}</div>}

          {loading ? (
            <div className="pguh-public-empty"><p>Loading vacancies…</p></div>
          ) : filtered.length === 0 ? (
            <div className="pguh-public-empty">
              <FaBriefcase aria-hidden="true" />
              <h2>{search ? "No matching vacancies" : "There are currently no vacancies"}</h2>
              <p>{search ? "Try another search." : "New opportunities will appear here when they are published."}</p>
            </div>
          ) : (
            <div className="pguh-public-vacancy-list">
              {filtered.map((vacancy) => (
                <article key={vacancy.id} className="pguh-public-vacancy-card">
                  <div>
                    <span className="pguh-public-open-badge">Open</span>
                    <h2>{vacancy.title}</h2>
                    <p>{vacancy.description || "Further information is available on the vacancy page."}</p>
                    <div className="pguh-public-vacancy-card__meta">
                      {vacancy.department && <span><FaBriefcase aria-hidden="true" /> {vacancy.department}</span>}
                      {vacancy.location && <span><FaMapMarkerAlt aria-hidden="true" /> {vacancy.location}</span>}
                      <span><FaCalendarAlt aria-hidden="true" /> {vacancy.closing_date ? `Closes ${formatDate(vacancy.closing_date)}` : "Open until filled"}</span>
                    </div>
                  </div>
                  <Link to={`/vacancies/${vacancy.id}`}>View vacancy</Link>
                </article>
              ))}
            </div>
          )}
        </div>
      </main>
      <PublicFooter />
    </div>
  );
}
